// public/v3/src/lib/month-pace.js — pure pacing math for the Time Logs
// month-progress summary and the "At risk" lens (the Month Queue concept,
// merged into Time Logs to keep one working surface).
//
// CJS (Jest-loadable) like ccm-tier-progress.js. Pure functions only; the
// caller injects day-of-month so past/future months stay deterministic.
//
// The point of "at risk" vs plain "below target": below-target is a fact
// about now, at-risk is a projection about month-end. A patient at 12/20 min
// on the 5th is fine; the same 12/20 on the 27th is about to be unbillable.
// Surfacing the projection mid-month flattens the end-of-month crunch.

const STATUS_RANK = { complete: 0, 'on-pace': 1, untouched: 2, 'at-risk': 3 };

// paceStatus(mins, target, dayOfMonth, daysInMonth) →
//   'complete'  — target met (or no target)
//   'untouched' — zero minutes logged
//   'at-risk'   — straight-line projection (mins ÷ elapsed fraction) lands
//                 below target by month-end
//   'on-pace'   — started and projecting to make it
// For a CLOSED month pass dayOfMonth = daysInMonth: projection collapses to
// "ended below target" → 'at-risk', which reads as "missed".
function paceStatus(mins, target, dayOfMonth, daysInMonth) {
  const m = Math.max(0, Number(mins) || 0);
  const t = Number(target) || 0;
  if (t <= 0 || m >= t) return 'complete';
  if (m === 0) return 'untouched';
  const days = Math.max(1, Number(daysInMonth) || 31);
  const day = Math.min(Math.max(1, Number(dayOfMonth) || 1), days);
  const projected = m * (days / day);
  return projected < t ? 'at-risk' : 'on-pace';
}

// Required minutes-per-remaining-day to still hit the target. 0 when met;
// grows as days run out — the urgency sort key (higher = work first). The
// 0.5-day floor keeps the last day finite and enormous instead of Infinity.
function urgencyPerDay(mins, target, dayOfMonth, daysInMonth) {
  const m = Math.max(0, Number(mins) || 0);
  const t = Number(target) || 0;
  const gap = Math.max(0, t - m);
  if (gap === 0) return 0;
  const days = Math.max(1, Number(daysInMonth) || 31);
  const day = Math.min(Math.max(0, Number(dayOfMonth) || 0), days);
  return gap / Math.max(0.5, days - day);
}

// Row-level rollup across a patient's program slots ([{mins, target}, …]):
// worst slot status wins (at-risk > untouched > on-pace > complete), urgency
// is the max across slots. Empty slots (APCM flat-rate months) → null.
function rowPace(slots, dayOfMonth, daysInMonth) {
  const list = Array.isArray(slots) ? slots.filter(s => s && (Number(s.target) || 0) > 0) : [];
  if (!list.length) return null;
  let status = 'complete';
  let urgency = 0;
  for (const s of list) {
    const st = paceStatus(s.mins, s.target, dayOfMonth, daysInMonth);
    if (STATUS_RANK[st] > STATUS_RANK[status]) status = st;
    const u = urgencyPerDay(s.mins, s.target, dayOfMonth, daysInMonth);
    if (u > urgency) urgency = u;
  }
  return { status, urgency };
}

module.exports = { STATUS_RANK, paceStatus, urgencyPerDay, rowPace };
